<template>
  <section :style="styles" v-loading="loading" class="flex flex-col h-full">
    <!-- First child, so the Teleport target exists before any dropdown mounts.
         Kept inside this section (rather than in index.html) so its z-index 1999
         stays under .el-loading-mask (2000). -->
    <div id="dropdown-teleport-root" class="dropdown-teleport-root"></div>
    <!-- Hidden while the session is expired so portfolio data isn't left
         readable behind the overlay on an unattended screen. -->
    <template v-if="onLoad && !isAppIdle">
      <main-header v-if="showHeader" />

      <main class="h-full overflow-auto">
        <router-view />
      </main>
    </template>
    <idle-view />
  </section>
</template>

<script>
import { basicPages } from '@/core/config'
import { useGeneralStore } from '@/stores/general'
import { useAuthStore } from '@/stores/auth'
import { useInvestorStore } from '@/stores/investor'
import { useCompanyStore } from '@/stores/company'
import MainHeader from '@/components/layout/MainHeader'
import { useIdleSession } from '@/composables/useIdleSession'

import { routesWithoutHeader } from '@/router'
import formatterService from '@/core/format.service'
import { setLocale } from '@vee-validate/i18n'

export default {
  components: {
    MainHeader
  },

  setup () {
    const generalStore = useGeneralStore()
    const authStore = useAuthStore()
    const investorStore = useInvestorStore()
    const companyStore = useCompanyStore()

    /* Must be set up here rather than in IdleView so the timer runs for the
       whole life of the app, not just while the overlay happens to be mounted. */
    useIdleSession()

    return {
      generalStore,
      authStore,
      investorStore,
      companyStore
    }
  },

  data () {
    return {
      onLoad: false
    }
  },

  computed: {
    loading () {
      return this.generalStore.loading
    },
    token () {
      return this.authStore.token
    },
    currentInvestor () {
      return this.investorStore.currentInvestor
    },
    whiteLabel () {
      return this.companyStore.whiteLabel
    },

    showHeader () {
      return !routesWithoutHeader.includes(this.$route.name)
    },

    styles () {
      return `--color-primary: ${this.whiteLabel.color}; --color-secondary: ${this.whiteLabel.secondaryColor}`
    },

    isBasicPage () {
      return basicPages.includes(this.$route.name)
    },

    isAppIdle () {
      return this.generalStore.isIdle
    }
  },

  methods: {
    changeDeviceProperty (value) {
      this.generalStore.changeDeviceProperty(value)
    },
    getGlobalData () {
      return this.generalStore.getGlobalData()
    }
  },

  created () {
    formatterService.updateFormatLocale(this.$i18n.locale)
    if (this.isBasicPage) this.onLoad = true
    // get main app data in case of logged in APP start
    if (this.token) this.getGlobalData().finally(() => (this.onLoad = true))

    /* Messages themselves are configured once in plugins/vee-validate.js; this
       only keeps vee-validate's active locale in step with the app's. */
    setLocale(this.$i18n.locale)
  },

  mounted () {
    this.changeDeviceProperty(window.innerWidth)
    window.onresize = () => {
      this.changeDeviceProperty(window.innerWidth)
    }
  },

  watch: {
    $route (to) {
      if (basicPages.includes(to.name)) this.onLoad = true
    },
    '$i18n.locale' (newLocale) {
      setLocale(newLocale)
    }
  }
}
</script>
